import { useCallback } from "react";
import { useCookieStore } from "@edreamsodigeo/frontend-core";

import { MEMBERSHIP_STATUS } from "./useUser/membershipStatus";

const SSO_REMEMBER_ME_COOKIE = "sso_remember_me";
const COOKIE_SEPARATOR = ";";
const THIRTEEN_MONTHS_EXPIRATION_IN_DAYS = 390;

export const LOGIN_METHODS = {
  google: "google",
  password: "mail",
  passwordless: "emailLogin",
  recognized: "recognized",
};

const encodeCookieData = (data) => encodeURIComponent(data);

const decodeCookie = (cookie) => {
  return decodeURIComponent(cookie);
};

export const buildCookie = (loginMethod, user) => {
  const hasActiveMembership =
    user?.membership?.status === MEMBERSHIP_STATUS.activated;

  return encodeCookieData(
    // This format must be kept for compatibility with frontend-html5, frontend-api and others
    [user?.email, user?.displayName, hasActiveMembership, loginMethod].join(
      COOKIE_SEPARATOR,
    ),
  );
};

export const buildRememberedUser = (cookie) => {
  if (cookie) {
    const parsedCookie = decodeCookie(cookie).split(COOKIE_SEPARATOR);

    return {
      email: parsedCookie[0],
      displayName: parsedCookie[1],
      membership:
        parsedCookie[2] === "true"
          ? { status: MEMBERSHIP_STATUS.activated }
          : null,
      accountType:
        parsedCookie[3] !== LOGIN_METHODS.password ? "social" : parsedCookie[3],
      loginMethod: parsedCookie[3],
    };
  } else {
    return null;
  }
};

export const useRememberMe = () => {
  const cookieStore = useCookieStore();
  const setRememberedUser = useCallback(
    (loginMethod, user) => {
      cookieStore.set(
        SSO_REMEMBER_ME_COOKIE,
        buildCookie(loginMethod, user),
        THIRTEEN_MONTHS_EXPIRATION_IN_DAYS,
      );
    },
    [cookieStore],
  );

  const deleteRememberedUser = useCallback(() => {
    cookieStore.delete(SSO_REMEMBER_ME_COOKIE);
  }, [cookieStore]);

  const getRememberedUser = useCallback(() => {
    const rememberedUser = buildRememberedUser(
      cookieStore.get(SSO_REMEMBER_ME_COOKIE),
    );

    // TODO(DPT-6592): temporal fix to ensure there are not users identified via sso with this account
    if (rememberedUser?.email === "crm.qa@edreamsodigeo.com") {
      deleteRememberedUser();
      return null;
    }

    return rememberedUser;
  }, [cookieStore, deleteRememberedUser]);

  return [getRememberedUser, setRememberedUser, deleteRememberedUser];
};
